import { DataCard, Empty, Icon, PageTitle, Spinner, cx } from "../../components/ui";
import { NAMA_RUBRIK, namaBulan } from "./logika";

// Beranda Raport: pilih term (chip), lalu kartu per kelas yang diajar trainer ini di cabang
// aktif. Kelas yang belum bisa ia isi tetap tampil, dengan sebabnya — supaya trainer tahu
// kenapa kelasnya "hilang" dan siapa yang harus bertindak.

const tglPendek = (detik) => new Date(detik * 1000).toLocaleDateString("id-ID", { day: "numeric", month: "short" });
const persen = (n, dari) => (dari ? Math.round((n / dari) * 100) : 0);

export function ChipTerm({ t, aktif, sekarang, onClick }) {
  return (
    <button
      type="button"
      aria-pressed={aktif}
      onClick={onClick}
      className={cx(
        "lift-press inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-bold",
        aktif
          ? "border-primary bg-primary text-white"
          : "border-slate-200 bg-white text-slate-600 hover:border-primary/60 hover:text-primary dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
      )}
    >
      {t.terkunci && !t.dibukaSampai && <Icon name="lock" className="text-base" />}
      {t.name}
      {sekarang && <span className={cx("text-[10px] font-black uppercase", aktif ? "text-white/80" : "text-primary")}>· berjalan</span>}
    </button>
  );
}

function KartuKelas({ k, urutan, onBuka }) {
  const bisa = k.pemegang.status === "kamu" && k.rubrik;
  const p = k.progres;
  const keterangan = !k.rubrik
    ? "Program kelas belum diisi admin, jadi belum bisa diraport."
    : k.pemegang.status === "belum-ditetapkan"
    ? "Kelas ini punya lebih dari satu trainer. Menunggu team leader menetapkan penanggung jawab raportnya."
    : k.pemegang.status === "trainer-lain"
    ? `Raport kelas ini diisi ${k.pemegang.nama || "trainer lain"}.`
    : "";
  const isi = (
    <>
      <span className="flex items-start justify-between gap-3">
        <span className="min-w-0">
          <span className="block truncate text-base font-black text-slate-900 dark:text-white">{k.nama}</span>
          <span className="block text-xs text-slate-500">
            {[k.program, k.level, k.sesi].filter(Boolean).join(" · ")}
          </span>
        </span>
        {k.rubrik && (
          <span className="shrink-0 rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-black text-primary dark:text-[#3987e5]">
            {NAMA_RUBRIK[k.rubrik]}
          </span>
        )}
      </span>
      {bisa && p ? (
        <span className="mt-4 block">
          {/* Hijau = sudah terbit; biru muda = siap terbit, menunggu diterbitkan. */}
          <span className="flex h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800" aria-hidden="true">
            <span className="block h-full bg-green-500 transition-[width] duration-300" style={{ width: `${persen(p.terbit, p.murid)}%` }} />
            <span className="block h-full bg-primary/50 transition-[width] duration-300" style={{ width: `${persen(p.siapTerbit, p.murid)}%` }} />
          </span>
          <span className="mt-2 block text-sm text-slate-600 dark:text-slate-300">
            <b>{p.terbit}</b> dari <b>{p.murid}</b> murid sudah terbit
          </span>
          <span className="block text-xs text-slate-400">
            {p.siapTerbit} siap terbit · {p.lengkap} terisi penuh ketiga bulannya
          </span>
          {p.menungguKelasUtama > 0 && (
            <span className="mt-1 block text-xs font-bold text-amber-700 dark:text-amber-300">
              {p.menungguKelasUtama} murid menunggu team leader memilih kelas utamanya
            </span>
          )}
        </span>
      ) : (
        <span className="mt-3 flex items-start gap-2 rounded-xl bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">
          <Icon name="info" className="text-base" />
          {keterangan}
        </span>
      )}
    </>
  );
  // flex-col: isi kartu yang berupa <button> tetap mulai dari atas — tombol bawaan peramban
  // menaruh isinya di tengah, sehingga kartu di baris yang sama tampak tak sejajar.
  const kelasDasar = cx(
    "anim-rise flex w-full flex-col rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm dark:border-slate-800 dark:bg-slate-900",
    urutan < 4 && `anim-d${urutan + 1}`
  );
  return bisa ? (
    <button type="button" onClick={onBuka} className={cx(kelasDasar, "lift-press hover:border-primary/50")}>
      {isi}
    </button>
  ) : (
    <div className={cx(kelasDasar, "opacity-90")}>{isi}</div>
  );
}

export default function Beranda({ terms, term, termSekarang, kelasSaya, galat, onPilihTerm, onBukaKelas }) {
  return (
    <div>
      <PageTitle
        title="Raport"
        subtitle="Isi nilai dan komentar raport murid di kelas yang kamu pegang, lalu terbitkan dan cetak di akhir term. Setiap ketukan langsung tersimpan."
      />

      <DataCard delay={1} className="mb-6 p-5">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Term</p>
        <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Pilih term">
          {terms.map((t) => (
            <ChipTerm key={t.id} t={t} aktif={term && term.id === t.id} sekarang={t.id === termSekarang} onClick={() => onPilihTerm(t.id)} />
          ))}
        </div>
        {term && (
          <p className="mt-3 text-sm text-slate-500">
            Bulan 1 {namaBulan(term.bulan[0])} · Bulan 2 {namaBulan(term.bulan[1])} · Bulan 3 {namaBulan(term.bulan[2])}
            {term.dibukaSampai
              ? ` · dibuka sementara oleh team leader sampai ${tglPendek(term.dibukaSampai)}`
              : term.terkunci ? " · terkunci, hanya bisa dilihat" : ""}
          </p>
        )}
      </DataCard>

      {galat ? (
        <Empty label={galat} />
      ) : !kelasSaya ? (
        <Spinner label="Memuat kelas…" />
      ) : !kelasSaya.kelas.length ? (
        <Empty label="Kamu belum memegang kelas yang memakai raport di cabang ini." />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {kelasSaya.kelas.map((k, i) => (
            <KartuKelas key={k.id} k={k} urutan={i} onBuka={() => onBukaKelas(k.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
